import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const NIVEL_COLOR = {
  error:   { bg: '#fee2e2', text: '#dc2626', label: '🔴 Error' },
  warning: { bg: '#fef9c3', text: '#d97706', label: '🟡 Warning' },
  info:    { bg: '#dbeafe', text: '#2563eb', label: '🔵 Info' },
};

const ORIGEN_LABEL = {
  frontend: '🖥️ Frontend',
  backend:  '⚙️ Backend',
};

const ErrorLogs = () => {
  const [logs,         setLogs]         = useState([]);
  const [tenants,      setTenants]      = useState([]);
  const [tenantSel,    setTenantSel]    = useState('');
  const [loadTenants,  setLoadTenants]  = useState(true);
  const [total,        setTotal]        = useState(0);
  const [loading,      setLoading]      = useState(false);
  const [purgando,     setPurgando]     = useState(false);
  const [detalle,      setDetalle]      = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  const [filtros, setFiltros] = useState({
    nivel:        '',
    origen:       '',
    fecha_inicio: format(new Date(new Date().setDate(1)), 'yyyy-MM-dd'),
    fecha_fin:    format(new Date(), 'yyyy-MM-dd'),
  });
  const [pagina, setPagina] = useState(0);
  const LIMIT = 50;

  // Carga inicial de tenants
  useEffect(() => {
    api.get('/error-logs?limit=1&offset=0')
      .then(({ data }) => {
        setTenants(data.tenants || []);
        if (data.tenants?.length > 0) setTenantSel(String(data.tenants[0].id));
      })
      .catch(() => {})
      .finally(() => setLoadTenants(false));
  }, []);

  const fetchLogs = useCallback(async () => {
    if (!tenantSel) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: LIMIT, offset: pagina * LIMIT, tenant_id: tenantSel });
      if (filtros.nivel)        params.append('nivel',        filtros.nivel);
      if (filtros.origen)       params.append('origen',       filtros.origen);
      if (filtros.fecha_inicio) params.append('fecha_inicio', filtros.fecha_inicio);
      if (filtros.fecha_fin)    params.append('fecha_fin',    filtros.fecha_fin);

      const { data } = await api.get(`/error-logs?${params}`);
      setLogs(data.logs);
      setTotal(data.total);
    } catch (error) {
      console.error('Error al cargar logs:', error);
    } finally {
      setLoading(false);
    }
  }, [filtros, pagina, tenantSel]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleFiltroChange = (e) => {
    setFiltros(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setPagina(0);
  };

  const handleTenantChange = (e) => {
    setTenantSel(e.target.value);
    setPagina(0);
  };

  const handlePurgar = () => {
    setConfirmAction({ onConfirm: async () => {
      setPurgando(true);
      try {
        const { data } = await api.delete('/error-logs?dias=30');
        toast.success(`Se eliminaron ${data.eliminados} registros.`);
        fetchLogs();
      } catch {
        toast.error('Error al purgar logs.');
      } finally {
        setPurgando(false);
      }
    }});
  };

  const totalPaginas = Math.ceil(total / LIMIT);

  return (
    <Layout>
      {/* Header */}
      <div className="page-header">
        <div>
          <h2>🔍 Logs de Errores</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: '0.25rem' }}>
            {total} registro{total !== 1 ? 's' : ''} encontrado{total !== 1 ? 's' : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn btn-secondary" onClick={fetchLogs} disabled={loading}>
            🔄 Actualizar
          </button>
          <button className="btn btn-danger" onClick={handlePurgar} disabled={purgando}>
            🗑️ Purgar &gt;30 días
          </button>
        </div>
      </div>

      {/* Selector de empresa */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <label style={{ fontWeight: '600', fontSize: '0.875rem', color: '#374151', marginRight: '0.75rem' }}>
          🏢 Ver logs de:
        </label>
        {loadTenants ? (
          <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Cargando empresas...</span>
        ) : (
          <select value={tenantSel} onChange={handleTenantChange} style={{ ...selectStyle, minWidth: '280px', display: 'inline-block', width: 'auto' }}>
            {tenants.map(t => (
              <option key={t.id} value={t.id}>{t.nombre_negocio}</option>
            ))}
          </select>
        )}
      </div>

      {/* Filtros */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem' }}>
          <div>
            <label style={labelStyle}>Nivel</label>
            <select name="nivel" value={filtros.nivel} onChange={handleFiltroChange} style={selectStyle}>
              <option value="">Todos</option>
              <option value="error">Error</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
            </select>
          </div>

          <div>
            <label style={labelStyle}>Origen</label>
            <select name="origen" value={filtros.origen} onChange={handleFiltroChange} style={selectStyle}>
              <option value="">Todos</option>
              <option value="frontend">Frontend</option>
              <option value="backend">Backend</option>
            </select>
          </div>

          <div>
            <label style={labelStyle}>Desde</label>
            <input type="date" name="fecha_inicio" value={filtros.fecha_inicio} onChange={handleFiltroChange} style={selectStyle} />
          </div>

          <div>
            <label style={labelStyle}>Hasta</label>
            <input type="date" name="fecha_fin" value={filtros.fecha_fin} onChange={handleFiltroChange} style={selectStyle} />
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div className="text-center" style={{ padding: '3rem' }}>
            <div className="loading"></div>
            <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>Cargando logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
            <span style={{ fontSize: '3rem' }}>✅</span>
            <p style={{ marginTop: '1rem' }}>No hay errores registrados con estos filtros.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: 'var(--background)', borderBottom: '2px solid var(--border)' }}>
                  {['Fecha', 'Nivel', 'Origen', 'Endpoint', 'HTTP', 'Mensaje', ''].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => {
                  const nivel  = NIVEL_COLOR[log.nivel]  || NIVEL_COLOR.error;
                  return (
                    <tr
                      key={log.id}
                      style={{ background: i % 2 === 0 ? 'white' : 'var(--background)', borderBottom: '1px solid var(--border)' }}
                    >
                      <td style={tdStyle}>
                        {format(new Date(log.created_at), 'dd/MM/yy HH:mm', { locale: es })}
                      </td>
                      <td style={tdStyle}>
                        <span style={{ background: nivel.bg, color: nivel.text, padding: '2px 8px', borderRadius: '12px', fontWeight: 600, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                          {nivel.label}
                        </span>
                      </td>
                      <td style={tdStyle}>{ORIGEN_LABEL[log.origen] || log.origen}</td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.8rem', color: '#6b7280' }}>
                        {log.metodo && <span style={{ color: '#2563eb', marginRight: '4px' }}>{log.metodo}</span>}
                        {log.endpoint || '—'}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        {log.codigo_http ? (
                          <span style={{ color: log.codigo_http >= 500 ? '#dc2626' : '#d97706', fontWeight: 600 }}>
                            {log.codigo_http}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={{ ...tdStyle, maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {log.mensaje}
                      </td>
                      <td style={tdStyle}>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => setDetalle(log)}
                        >
                          Ver
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginación */}
        {totalPaginas > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', padding: '1rem', borderTop: '1px solid var(--border)' }}>
            <button className="btn btn-secondary btn-sm" disabled={pagina === 0} onClick={() => setPagina(p => p - 1)}>
              ← Anterior
            </button>
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              Página {pagina + 1} de {totalPaginas}
            </span>
            <button className="btn btn-secondary btn-sm" disabled={pagina >= totalPaginas - 1} onClick={() => setPagina(p => p + 1)}>
              Siguiente →
            </button>
          </div>
        )}
      </div>

      {/* Modal detalle */}
      {detalle && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '640px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Detalle del Error #{detalle.id}</h3>
              <button onClick={() => setDetalle(null)}>✕</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <Row label="Fecha"    value={format(new Date(detalle.created_at), 'dd/MM/yyyy HH:mm:ss', { locale: es })} />
              <Row label="Nivel"    value={NIVEL_COLOR[detalle.nivel]?.label || detalle.nivel} />
              <Row label="Origen"   value={ORIGEN_LABEL[detalle.origen] || detalle.origen} />
              <Row label="Tenant"   value={detalle.tenant_nombre || '—'} />
              <Row label="Usuario"  value={detalle.usuario_email || '—'} />
              <Row label="IP"       value={detalle.ip || '—'} />
              <Row label="Endpoint" value={detalle.endpoint ? `${detalle.metodo || ''} ${detalle.endpoint}` : '—'} mono />
              <Row label="HTTP"     value={detalle.codigo_http || '—'} />
              <Row label="Archivo"  value={detalle.archivo ? `${detalle.archivo}${detalle.linea ? `:${detalle.linea}` : ''}` : '—'} mono />
              <div>
                <span style={labelStyle}>Mensaje</span>
                <pre style={preStyle}>{detalle.mensaje}</pre>
              </div>
              {detalle.stack_trace && (
                <div>
                  <span style={labelStyle}>Stack Trace</span>
                  <pre style={{ ...preStyle, maxHeight: '200px', overflowY: 'auto' }}>{detalle.stack_trace}</pre>
                </div>
              )}
              {detalle.datos_extra && (
                <div>
                  <span style={labelStyle}>Datos Extra</span>
                  <pre style={{ ...preStyle, maxHeight: '150px', overflowY: 'auto' }}>
                    {typeof detalle.datos_extra === 'string'
                      ? detalle.datos_extra
                      : JSON.stringify(detalle.datos_extra, null, 2)}
                  </pre>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDetalle(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {confirmAction && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Confirmar acción</h3>
              <button onClick={() => setConfirmAction(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: '#374151' }}>¿Confirmas esta acción?</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setConfirmAction(null)}>No</button>
              <button className="btn btn-primary" onClick={() => { confirmAction.onConfirm(); setConfirmAction(null); }}>Sí</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

const Row = ({ label, value, mono }) => (
  <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
    <span style={{ ...labelStyle, minWidth: '90px', flexShrink: 0 }}>{label}</span>
    <span style={{ fontSize: '0.875rem', fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>{value}</span>
  </div>
);

const labelStyle = {
  display: 'block',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '0.25rem',
};

const selectStyle = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--border)',
  borderRadius: '8px',
  fontSize: '0.875rem',
  background: 'var(--background)',
};

const thStyle = {
  padding: '0.75rem 1rem',
  textAlign: 'left',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '0.75rem 1rem',
  verticalAlign: 'middle',
};

const preStyle = {
  background: '#f8fafc',
  border: '1px solid var(--border)',
  borderRadius: '8px',
  padding: '0.75rem',
  fontSize: '0.8rem',
  fontFamily: 'monospace',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  margin: 0,
};

export default ErrorLogs;
